import React, { forwardRef, type ReactNode } from 'react';

import classnames from 'classnames';

import { type FloatingProps } from '../Floating/Floating.types';
import { FloatingArrow } from '../Floating/FloatingArrow';
import { FloatingContainer } from '../Floating/FloatingContainer';
import { FloatingContent } from '../Floating/FloatingContent';
import { FloatingPortal } from '../Floating/FloatingPortal';
import { FloatingTrigger } from '../Floating/FloatingTrigger';
import { useFloatingTriggerProps } from '../Floating/useFloatingTriggerProps';

import styles from './Tooltip.module.scss';

export type TooltipProps = Omit<FloatingProps, 'title' | 'stickyOnTopWhileScrolling'> & {
    /**
     * How the text should be aligned.
     */
    textAlignment?: 'center' | 'left' | 'right';
    /**
     * The content shown in the tooltip element
     */
    title: ReactNode;
    /**
     * Whether the tooltip element should be shown or hidden.
     * *Note*: `title` with a falsy value will always hide the tooltip.
     */
    hidden?: boolean;
    /**
     * Tooltips don't work on disabled elements because their event handlers are blocked.
     * Use this property to explicitly inform the tooltip that the child is disabled so it can work properly.
     */
    disabled?: boolean;
};

const TOOLTIP_DEFAULT_TEXT_ALIGNMENT = 'left';

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
    (
        {
            children,
            placement,
            open,
            onOpenChange,
            align,
            className,
            hidden,
            noArrow,
            mouseEnterDelay,
            mouseLeaveDelay,
            mouseRestDelay,
            animationDuration,
            destroyOnHide = true,
            textAlignment = TOOLTIP_DEFAULT_TEXT_ALIGNMENT,
            title,
            trigger,
            style,
            disabled,
            getPopupContainer,
            popupRef,
            role = 'tooltip',
            keepTriggerFocused,
            ...rest
        }: TooltipProps,
        ref
    ) => {
        const [triggerProps, contentProps] = useFloatingTriggerProps(rest);
        const mergedHidden = Boolean(hidden || !title);
        return (
            <FloatingContainer
                placement={placement}
                open={open}
                onOpenChange={onOpenChange}
                trigger={trigger}
                mouseEnterDelay={mouseEnterDelay}
                mouseLeaveDelay={mouseLeaveDelay}
                align={align}
                mouseRestDelay={mouseRestDelay}
                animationDuration={animationDuration}
                role={role}
            >
                <FloatingTrigger ref={ref} {...triggerProps} visuallyDisabled={disabled}>
                    {children}
                </FloatingTrigger>
                {!mergedHidden && (
                    <FloatingPortal
                        getPopupContainer={getPopupContainer}
                        destroyOnHide={destroyOnHide}
                        ref={popupRef}
                        className={styles.portal}
                        {...contentProps}
                    >
                        <FloatingContent
                            className={classnames(styles.content, className)}
                            shouldTrapFocus={keepTriggerFocused}
                            style={{
                                textAlign: textAlignment,
                                ...style
                            }}
                        >
                            {title}
                            {!noArrow && <FloatingArrow />}
                        </FloatingContent>
                    </FloatingPortal>
                )}
            </FloatingContainer>
        );
    }
);

Tooltip.displayName = 'Tooltip';
